import { escapeRegExp, isString, keyBy } from 'lodash';
import { Text } from '@shared/components/Text';
import { Markdown } from '@shared/components/markdown';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { FormattedTokenText } from './FormattedTokenText';
import { FormattedTextProps } from './props';
export { FormattedTokenText } from './FormattedTokenText';

/*
    Formats text like "#hello @all king@coastapp.com is my http://email.com"
    Note: this uses API specific data (API.ChatMetaTokenFragment), but I figured
    it should be under standard components since it might be useful later
    in other contexts too.
*/
export function FormattedText({
  tokens,
  children: text,
  highlightColor = Colors.v2.purple,
  markdown = false,
  ...otherProps
}: FormattedTextProps) {
  if (markdown) {
    return (
      <Markdown tokens={tokens} highlightColor={highlightColor} markdown={markdown} {...otherProps}>
        {text}
      </Markdown>
    );
  }

  // If there are no tokens, the regex below would just spread the string
  // in pieces (regex /(|)/ turns "asd" => ["a", "s", "d"] ), which would
  // make ex emojis not work
  if (!tokens.length || !isString(text)) {
    return <Text {...otherProps}>{text}</Text>;
  }

  const tokensFromValue: Dict<API.ChatMetaTokenFragment> = keyBy(tokens, 'value');
  // Split text into segments based on the token values
  // "hello @all done" => ["hello ", "@all", " done"]
  // For reference: https://stackoverflow.com/q/54330321/9117995
  const values = tokens
    .map(token => token.value)
    // Need to escape strings, otherwise eg links with "?" fail
    // https://stackoverflow.com/a/29700268
    .map(escapeRegExp);
  // Create regex that finds the token values that we want to format
  const regex = new RegExp(`(${values.join('|')})`);
  // Remove all empty strings (eg when splitting "@all" => ["", "@all", ""])
  const substrings = text.split(regex).filter(split => !!split);

  return (
    <Text>
      {substrings.map((substring: string, index: number) => (
        <FormattedTokenText
          {...otherProps}
          key={index}
          token={tokensFromValue[substring]}
          highlightColor={highlightColor}
        >
          {substring}
        </FormattedTokenText>
      ))}
    </Text>
  );
}
